import { ENTITY_SPECS } from '../../models/EntitySpecs';
import type { EntitySpec } from '../../models/Models';
import type { AnyEntity } from './types';

/** Best-effort name for an entity row, falling back to `<Singular> #<id>`. */
export const displayName = (item: AnyEntity, singular = 'Item'): string =>
  String((item as Record<string, unknown>).name ?? `${singular} #${item.id}`);

/**
 * Normalise the many shapes a "boolean" field can take in the legacy schema
 * (real bool, 'true'/'false', '1'/'0', 'yes'/'no', ...) into a tri-state
 * value: '' (unset), 'true' or 'false'.
 */
export const normalizeBoolean = (v: unknown): '' | 'true' | 'false' => {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  const s = String(v).trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(s)) return 'true';
  if (['false', '0', '-1', 'no', 'n'].includes(s)) return 'false';
  return '';
};

export const renderValue = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '-';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  const s = String(v);
  // Trim noisy zero-time component on date strings (e.g. "2015-01-01T00:00:00").
  const dateMatch = s.match(/^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.0+)?$/);
  if (dateMatch) return dateMatch[1];
  return s;
};

/** Format an ISO datetime string for display (local timezone, minute precision).
 *  The API stores datetimes in UTC; ensure they are parsed as UTC by appending
 *  'Z' when no timezone info is present.
 */
export const renderDateTime = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '-';
  const s = String(v);
  // Append 'Z' if the string has no timezone designator so the browser
  // treats it as UTC rather than local time.
  const utcStr = /[Z+\-]\d*$/.test(s) ? s : `${s}Z`;
  const d = new Date(utcStr);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Several "boolean" fields are stored as Text in the DB, so values arrive as
// the strings "true"/"false" (or "0"/"1"). Coerce here so the Profile view
// always shows Yes/No for fields the spec marks as boolean.
export const renderBoolean = (v: unknown): string => {
  const norm = normalizeBoolean(v);
  if (norm === 'true') return 'Yes';
  if (norm === 'false') return 'No';
  return v === null || v === undefined || v === '' ? '-' : String(v);
};

/** Shared sx for the small outlined icon buttons used in the hero + relation toolbars. */
export const iconButtonSx = (variant: 'edit' | 'delete') => ({
  border: '1px solid #DEE3EA',
  borderRadius: '8px',
  color: '#5C6B7A',
  '&:hover':
    variant === 'edit'
      ? { color: '#009DE0', borderColor: '#009DE0', bgcolor: 'rgba(0,157,224,0.06)' }
      : { color: '#E30613', borderColor: '#E30613', bgcolor: 'rgba(227,6,19,0.04)' },
});

/** Resolve which entity spec a backend path belongs to, for inline rendering. */
export const specForPath = (path: string): EntitySpec | undefined =>
  ENTITY_SPECS.find((s) => s.path === path);
